import React, { useEffect, useMemo } from 'react';
import { Tabs, Tab } from '@mui/material';
import PageHeaderTabs from '../../../../components/shared/page-header-tabs';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../hooks/hooks';
import { getVisibleSettingsTabs, SettingsTabKey } from '../../tabs';

interface SettingsHeaderProps {
    activeTab: SettingsTabKey;
    loading?: boolean;
}

export const SettingsHeader: React.FC<SettingsHeaderProps> = ({ activeTab, loading }) => {
    const profile = useAppSelector(store => store.profile);
    const navigate = useNavigate();

    const availableTabs = useMemo(
        () => getVisibleSettingsTabs(profile),
        [profile],
    );

    useEffect(() => {
        if (availableTabs.length === 0) {
            return;
        }

        const currentTabExists = availableTabs.some(tab => tab.key === activeTab);
        if (!currentTabExists) {
            navigate(availableTabs[0].route, { replace: true });
        }
    }, [availableTabs, activeTab, navigate]);

    const activeTabIndex = activeTab ? availableTabs.findIndex(tab => tab.key === activeTab) : 0;
    const value = activeTabIndex >= 0 ? activeTabIndex : 0;

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        const targetTab = availableTabs[newValue];
        if (targetTab) {
            navigate(targetTab.route);
        }
    };

    if (availableTabs.length === 0) {
        return <PageHeaderTabs headerLocalizationId='settings' loading={loading}>{null}</PageHeaderTabs>;
    }

    return (
        <PageHeaderTabs headerLocalizationId='settings' loading={loading}>
            <Tabs
                value={value}
                onChange={handleChange}
                variant='scrollable'
                scrollButtons='auto'>
                {availableTabs.map((tab, index) => (
                    <Tab
                        disableRipple
                        label={tab.name}
                        key={tab.key}
                        value={index}
                        sx={{
                            paddingTop: '12px',
                            paddingBottom: '12px',
                            paddingLeft: 0,
                            paddingRight: 0,
                            fontSize: '16px',
                            lineHeight: '24px',
                            color: 'var(--text-primary)',
                            '&.Mui-selected': {
                                color: 'var(--text-accent)',
                            },
                        }}
                    />
                ))}
            </Tabs>
        </PageHeaderTabs>
    );
};

export default SettingsHeader;
